import { useMemo, useState } from 'react'
import axios from 'axios'
import { Link, useLocation } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FolderTree, KeyRound, MoreHorizontal, RefreshCw, ShieldCheck, Sparkles, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { SearchableSelect } from '@/components/common/searchable-select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { DataTablePagination } from '@/components/common/data-table-pagination'
import { getErrorMessage } from '@/lib/api'
import { withSystemRoleCapabilityFallback } from '@/lib/route-access'
import { getServiceInstanceIdFromSearch, getServiceInstanceIdFromToken, withServiceInstanceId } from '@/lib/service-instance'
import { getUserDisplayName, getUserDropdownKeywords, getUserDropdownLabel, getUserRegistrationNumber } from '@/lib/user-display'
import { authApi } from '@/services/auth'
import { orgUserAdminApi } from '@/services/org-user-admin'

import { postsApi } from '@/services/posts'
import { useAuthStore } from '@/stores/auth-store'

const getStatusLabel = (status?: string | number, isCurrent?: boolean, choicesMap?: Map<number, string>): string => {
  if (isCurrent) return 'Current'
  const value = Number(String(status ?? '').trim())
  if (!Number.isNaN(value) && choicesMap?.has(value)) return choicesMap.get(value)!
  return String(status ?? '') || '-'
}

const STATUS_VALUE_BADGE_CLASSES: Record<number, string> = {
  0: 'border-amber-200 bg-amber-100 text-amber-800',
  1: 'border-emerald-200 bg-emerald-100 text-emerald-800',
  2: 'border-rose-200 bg-rose-100 text-rose-800',
  3: 'border-violet-200 bg-violet-100 text-violet-800',
  4: 'border-slate-300 bg-slate-200 text-slate-800',
  5: 'border-orange-200 bg-orange-100 text-orange-800',
}

const getStatusBadgeClass = (status?: string | number, isCurrent?: boolean): string => {
  if (isCurrent) return 'border-sky-200 bg-sky-100 text-sky-800'
  const value = Number(String(status ?? '').trim())
  return STATUS_VALUE_BADGE_CLASSES[value] ?? 'border-border bg-muted text-foreground'
}


const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '-'
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return '-'
  }

  return new Intl.DateTimeFormat('en', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed)
}

const getRecentSortScore = (createdOn?: string | null, fallbackId?: string | number | null): number => {
  if (createdOn) {
    const parsed = Date.parse(createdOn)
    if (!Number.isNaN(parsed)) {
      return parsed
    }
  }

  if (fallbackId !== undefined && fallbackId !== null) {
    const asNumber = Number(fallbackId)
    if (Number.isFinite(asNumber)) {
      return asNumber
    }
  }

  return 0
}

export default function RolesPage() {
  const location = useLocation()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [selectedUserId, setSelectedUserId] = useState('')
  const [selectedRoleId, setSelectedRoleId] = useState('')
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>('default')
  const [pendingAction, setPendingAction] = useState<{
    type: 'status' | 'delete'
    postId: string
    nextStatus?: number
    actionName: string
    label: string
  } | null>(null)
  const [confirmInput, setConfirmInput] = useState('')
  const [dialogNextStatus, setDialogNextStatus] = useState<number | undefined>(undefined)
  const [showAssignPanel, setShowAssignPanel] = useState(false)
  const accessToken = useAuthStore((state) => state.accessToken)
  const storedServiceInstanceId = useAuthStore((state) => state.serviceInstanceId)
  const currentPost = useAuthStore((state) => state.currentPost)
  const queryClient = useQueryClient()

  const statusChoicesQuery = useQuery({
    queryKey: ['post-status-choices'],
    queryFn: () => postsApi.getStatusChoices(),
    staleTime: Infinity,
  })
  const statusChoices = statusChoicesQuery.data ?? []
  const statusChoicesMap = useMemo(
    () => new Map(statusChoices.map((c) => [c.value, c.label] as [number, string])),
    [statusChoices],
  )
  const STATUS_FILTER_OPTIONS = useMemo(() => [
    { value: 'default', label: 'Pending + Approved (default)' },
    { value: 'all', label: 'All statuses' },
    ...statusChoices.map((c) => ({ value: String(c.value), label: c.label })),
  ], [statusChoices])
  const statusFilterToValues = (filter: string): number[] | undefined => {
    if (filter === 'default') return undefined
    if (filter === 'all') return statusChoices.map((c) => c.value)
    const numericValue = Number(filter)
    if (Number.isFinite(numericValue) && statusChoices.some((c) => c.value === numericValue)) {
      return [numericValue]
    }
    return undefined
  }

  const currentPostServiceInstanceId = currentPost?.service_instance
    ? String(currentPost.service_instance)
    : currentPost?.service_instance_id
      ? String(currentPost.service_instance_id)
      : null
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
    || currentPostServiceInstanceId
    || storedServiceInstanceId
    || getServiceInstanceIdFromToken(accessToken)
  const currentCapabilities = currentPost?.capabilities
  const rolePermissionCapabilities = useMemo(() => {
    const permissions = currentPost?.roles_data?.permissions
    if (!permissions || typeof permissions !== 'object') {
      return undefined
    }

    const normalized: Record<string, boolean> = {}
    for (const [key, value] of Object.entries(permissions as Record<string, unknown>)) {
      normalized[key] = value === true || value === 1 || String(value).toLowerCase() === 'true'
    }
    return normalized
  }, [currentPost?.roles_data?.permissions])
  const effectiveCapabilities = useMemo(() => {
    if (!currentCapabilities && !rolePermissionCapabilities) {
      return withSystemRoleCapabilityFallback(currentPost, undefined)
    }
    return withSystemRoleCapabilityFallback(currentPost, {
      ...(rolePermissionCapabilities ?? {}),
      ...(currentCapabilities ?? {}),
    })
  }, [currentCapabilities, currentPost, rolePermissionCapabilities])
  const canManageRoles = Boolean(effectiveCapabilities?.can_manage_roles)
  const canAssignUserRoles = Boolean(effectiveCapabilities?.can_assign_user_roles)

  const assignedRolesQuery = useQuery({
    queryKey: ['assigned-roles', serviceInstanceId, page, pageSize, selectedStatusFilter],
    queryFn: () => postsApi.getAssignedRolesPage({
      orgInstanceId: serviceInstanceId!,
      serviceInstanceId: serviceInstanceId!,
      page,
      pageSize,
      status: statusFilterToValues(selectedStatusFilter),
    }),
    enabled: !!serviceInstanceId,
    staleTime: 30_000,
  })

  const isAssignedRolesForbidden =
    axios.isAxiosError(assignedRolesQuery.error) &&
    (assignedRolesQuery.error.response?.status === 401 || assignedRolesQuery.error.response?.status === 403)

  const assignableUsersQuery = useQuery({
    queryKey: ['org-user-admin-list', 'roles-page', serviceInstanceId],
    queryFn: () => orgUserAdminApi.list({ includeInactive: false, serviceInstanceId, orgInstanceId: serviceInstanceId }),
    enabled: !!serviceInstanceId && canAssignUserRoles && showAssignPanel,
    staleTime: 60_000,
  })

  const assignableRolesQuery = useQuery({
    queryKey: ['org-user-admin-roles', 'roles-page', serviceInstanceId],
    queryFn: () => orgUserAdminApi.listRoles({
      serviceInstanceId: serviceInstanceId || undefined,
      orgInstanceId: serviceInstanceId || undefined,
    }),
    enabled: !!serviceInstanceId && canAssignUserRoles && showAssignPanel,
    staleTime: 60_000,
  })

  const assignRoleMutation = useMutation({
    mutationFn: async () => {
      if (!selectedUserId || !selectedRoleId) {
        throw new Error('Select a user and role before assigning.')
      }

      return orgUserAdminApi.assignRole({
        userId: selectedUserId,
        roleId: selectedRoleId,
        serviceInstanceId: serviceInstanceId || undefined,
      })
    },
    onSuccess: async () => {
      toast.success('Role assigned successfully')
      setSelectedUserId('')
      setSelectedRoleId('')
      setShowAssignPanel(false)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['assigned-roles'] }),
        queryClient.invalidateQueries({ queryKey: ['org-user-admin-list', 'roles-page'] }),
        queryClient.invalidateQueries({ queryKey: ['org-user-admin-roles', 'roles-page'] }),
      ])
      await queryClient.refetchQueries({ queryKey: ['assigned-roles'], type: 'active' })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const updateAssignmentStatusMutation = useMutation({
    mutationFn: ({ id, nextStatus }: { id: string; nextStatus: number }) => postsApi.updateAssignmentStatus(id, nextStatus),
    onSuccess: async (_, variables) => {
      toast.success(variables.nextStatus === 1 ? 'Role assignment approved' : 'Role assignment set to pending')
      setPendingAction(null)
      setConfirmInput('')
      await authApi.refreshAuthState().catch(() => undefined)
      await queryClient.invalidateQueries({ queryKey: ['assigned-roles'] })
      await queryClient.refetchQueries({ queryKey: ['assigned-roles'], type: 'active' })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const deleteAssignmentMutation = useMutation({
    mutationFn: (id: string) => orgUserAdminApi.removeAssignment(id),
    onSuccess: async () => {
      toast.success('Role assignment deleted')
      setPendingAction(null)
      setConfirmInput('')
      await authApi.refreshAuthState().catch(() => undefined)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['assigned-roles'] }),
        queryClient.invalidateQueries({ queryKey: ['org-user-admin-list', 'roles-page'] }),
        queryClient.invalidateQueries({ queryKey: ['org-user-admin-roles', 'roles-page'] }),
      ])
      await queryClient.refetchQueries({ queryKey: ['assigned-roles'], type: 'active' })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const assignedRoles = useMemo(() => {
    const rows = assignedRolesQuery.data?.results ?? []
    return [...rows].sort((a, b) => {
      const scoreA = getRecentSortScore(a.created_on, a.id)
      const scoreB = getRecentSortScore(b.created_on, b.id)
      return scoreB - scoreA
    })
  }, [assignedRolesQuery.data?.results])
  const activeRoleCount = useMemo(
    () => assignedRoles.filter((row) => String(row.status) === '1').length,
    [assignedRoles],
  )
  const assignableUsers = useMemo(() => {
    const users = [...(assignableUsersQuery.data ?? [])]
    users.sort((a, b) => {
      const scoreA = getRecentSortScore((a as { created_on?: string | null }).created_on, a.user_id)
      const scoreB = getRecentSortScore((b as { created_on?: string | null }).created_on, b.user_id)
      return scoreB - scoreA
    })

    return users.map((user) => ({
      id: String(user.user_id),
      name: getUserDisplayName(user, user.user_id),
      registrationNumber: getUserRegistrationNumber(user),
      raw: user,
    }))
  }, [assignableUsersQuery.data])
  const assignableUserOptions = useMemo(
    () => assignableUsers.map((user) => ({
      value: user.id,
      label: getUserDropdownLabel(user.raw, user.id),
      keywords: `${user.name} ${user.registrationNumber} ${getUserDropdownKeywords(user.raw)}`,
    })),
    [assignableUsers],
  )
  const assignableRoles = useMemo(() => {
    const roles = [...(assignableRolesQuery.data ?? [])]
    roles.sort((a, b) => {
      const scoreA = getRecentSortScore((a as { created_on?: string | null }).created_on, a.id)
      const scoreB = getRecentSortScore((b as { created_on?: string | null }).created_on, b.id)
      return scoreB - scoreA
    })
    return roles
  }, [assignableRolesQuery.data])

  return (
    <div className="space-y-3 pb-1">
      <section>
        <Card className="overflow-hidden rounded-[18px] border-white/70 bg-[linear-gradient(135deg,rgba(255,255,255,0.97),rgba(236,253,245,0.92))] shadow-[0_14px_30px_-28px_rgba(15,23,42,0.45)]">
          <CardContent className="space-y-3 p-3 sm:p-4">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div className="space-y-1">
                <Badge className="rounded-full border-0 bg-emerald-500/10 px-2 py-0.5 text-[11px] text-emerald-700 hover:bg-emerald-500/10">
                  <Sparkles className="mr-1.5 h-3 w-3" />
                  Access control
                </Badge>
                <h1 className="text-xl font-semibold tracking-tight text-foreground">Role Assignments</h1>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row">
                {canManageRoles ? (
                  <>
                    <Button variant="outline" size="sm" className="rounded-lg" asChild>
                      <Link to={withServiceInstanceId('/org/features', serviceInstanceId)}>
                        <KeyRound className="mr-2 h-4 w-4" />
                        Manage Features
                      </Link>
                    </Button>
                    <Button variant="outline" size="sm" className="rounded-lg" asChild>
                      <Link to={withServiceInstanceId('/org/manage-roles', serviceInstanceId)}>
                        <FolderTree className="mr-2 h-4 w-4" />
                        Manage Roles
                      </Link>
                    </Button>
                  </>
                ) : null}
              </div>
            </div>

            <div className="grid gap-2 sm:grid-cols-3">
              <div className="rounded-lg border border-border/60 bg-background/60 px-3 py-2">
                <p className="text-[11px] text-muted-foreground">Visible role assignments</p>
                <p className="text-lg font-semibold text-foreground">{assignedRolesQuery.data?.count ?? 0}</p>
              </div>
              <div className="rounded-lg border border-border/60 bg-background/60 px-3 py-2">
                <p className="text-[11px] text-muted-foreground">Active on page</p>
                <p className="text-lg font-semibold text-foreground">{activeRoleCount}</p>
              </div>
              <div className="rounded-lg border border-border/60 bg-background/60 px-3 py-2">
                <p className="text-[11px] text-muted-foreground">Current role</p>
                <p className="text-sm font-semibold text-foreground">{currentPost?.role_name || 'None selected'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      <Card className="rounded-[20px] border-white/70 bg-card/88 shadow-[0_16px_40px_-34px_rgba(15,23,42,0.5)] backdrop-blur">
        <CardContent className="p-3 sm:p-4">
          {!serviceInstanceId ? (
            <div className="flex h-40 items-center justify-center rounded-[24px] border border-border/70 bg-background/75 text-center text-muted-foreground">
              Missing organization context. Open this page with a service_instance_id query parameter.
            </div>
          ) : (
            <div className="space-y-3">
              {!isAssignedRolesForbidden ? (
                <div className="grid gap-2 rounded-2xl border border-border/70 bg-muted/15 p-3 sm:grid-cols-[200px_minmax(0,1fr)] sm:items-end">
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium text-foreground">Status filter</p>
                    <Select
                      value={selectedStatusFilter}
                      onValueChange={(value) => {
                        setSelectedStatusFilter(value)
                        setPage(1)
                      }}
                    >
                      <SelectTrigger aria-label="Filter role assignments by status">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_FILTER_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Uses the posts table status field. Default shows Pending and Approved.
                  </p>
                </div>
              ) : null}

              {canAssignUserRoles ? (
                <div className="rounded-2xl border border-border/70 bg-muted/15 p-3">
                  {!showAssignPanel ? (
                    <Button variant="outline" size="sm" className="rounded-lg" onClick={() => setShowAssignPanel(true)}>
                      Assign Role
                    </Button>
                  ) : (
                  <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto_auto] lg:items-end">
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-foreground">User</p>
                      <SearchableSelect
                        value={selectedUserId}
                        onValueChange={setSelectedUserId}
                        options={assignableUserOptions}
                        placeholder="Select a user"
                        searchPlaceholder="Search users"
                        emptyText="No users found."
                        disabled={assignableUsersQuery.isLoading || assignRoleMutation.isPending}
                        ariaLabel="Assign user"
                      />
                    </div>

                    <div className="space-y-2">
                      <p className="text-sm font-medium text-foreground">Role</p>
                      <Select value={selectedRoleId} onValueChange={setSelectedRoleId} disabled={assignableRolesQuery.isLoading || assignRoleMutation.isPending}>
                        <SelectTrigger aria-label="Assign role">
                          <SelectValue placeholder="Select an organisation role" />
                        </SelectTrigger>
                        <SelectContent>
                          {assignableRoles.map((role) => (
                            <SelectItem key={role.id} value={String(role.id)}>
                              {`${role.name}${role.role_level_label ? ` (${role.role_level_label})` : role.role_level ? ` (L${role.role_level})` : ''}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <Button
                      onClick={() => assignRoleMutation.mutate()}
                      disabled={!selectedUserId || !selectedRoleId || assignRoleMutation.isPending || assignableUsersQuery.isLoading || assignableRolesQuery.isLoading}
                      className="min-w-[140px]"
                    >
                      Assign role
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setSelectedUserId('')
                        setSelectedRoleId('')
                        setShowAssignPanel(false)
                      }}
                      disabled={assignRoleMutation.isPending}
                    >
                      Cancel
                    </Button>
                  </div>
                  )}
                </div>
              ) : null}

              {isAssignedRolesForbidden ? (
                <div className="flex h-40 items-center justify-center rounded-[24px] border border-border/70 bg-background/75 px-6 text-center text-muted-foreground">
                  Your current role can view role access status, but it cannot list organization role assignments for this organization.
                </div>
              ) : assignedRolesQuery.error ? (
                <div className="flex h-40 flex-col items-center justify-center rounded-[24px] border border-destructive/20 bg-destructive/5 px-6 text-center">
                  <p className="mb-2 text-destructive">Failed to load assigned roles</p>
                  <p className="mb-4 text-sm text-muted-foreground">{getErrorMessage(assignedRolesQuery.error)}</p>
                  <Button variant="outline" onClick={() => assignedRolesQuery.refetch()}>
                    Try Again
                  </Button>
                </div>
              ) : assignedRolesQuery.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="flex items-center gap-3 rounded-xl border border-border/60 bg-muted/30 p-3">
                  <Skeleton className="h-8 w-8 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-44" />
                    <Skeleton className="h-3 w-28" />
                  </div>
                  <Skeleton className="h-8 w-28 rounded-full" />
                </div>
              ))}
            </div>
              ) : (
                <>
              <div className="overflow-hidden rounded-2xl border border-border/70 bg-background/75">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/35 hover:bg-muted/35">
                      <TableHead className="h-10 pl-4 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Name</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Role</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Created</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Status</TableHead>
                      <TableHead className="pr-4 text-right text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assignedRoles.length > 0 ? (
                      assignedRoles.map((post) => {
                        const isCurrent = currentPost ? String(currentPost.id) === String(post.id) : false
                        const userId = post.user_data?.id ? String(post.user_data.id) : ''
                        const userLabel = post.user_name || post.user_data?.name || 'Unknown user'
                        const roleLabel = post.roles_name || post.roles_data?.roles_name || 'Unassigned'
                        const roleLevelLabel = post.roles_data?.role_level_label || (post.roles_data?.role_level ? `L${post.roles_data.role_level}` : null)
                        const roleDisplayLabel = roleLevelLabel ? `${roleLabel} (${roleLevelLabel})` : roleLabel
                        const isUpdatingThisPost = updateAssignmentStatusMutation.isPending || deleteAssignmentMutation.isPending

                        return (
                          <TableRow key={post.id} className="border-border/60 transition-colors hover:bg-muted/25">
                            <TableCell className="pl-4 py-2.5 font-medium text-foreground">
                              {userId ? (
                                <Link
                                  to={withServiceInstanceId(`/org/users/${userId}`, serviceInstanceId)}
                                  className="text-primary underline-offset-4 hover:underline"
                                >
                                  {userLabel}
                                </Link>
                              ) : userLabel}
                            </TableCell>
                            <TableCell className="text-sm text-foreground">
                              <div>
                                <p>{roleDisplayLabel}</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">{formatDateTime(post.created_on)}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={`rounded-full text-[11px] ${getStatusBadgeClass(post.status, isCurrent)}`}>
                                {getStatusLabel(post.status, isCurrent, statusChoicesMap)}
                              </Badge>
                            </TableCell>
                            <TableCell className="pr-4 text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    disabled={isUpdatingThisPost || isCurrent}
                                    aria-label="Role assignment actions"
                                  >
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setPendingAction({ type: 'status', postId: post.id, actionName: 'change-status', label: `Change status for ${userLabel}` })
                                      setConfirmInput('')
                                      setDialogNextStatus(undefined)
                                    }}
                                    disabled={isUpdatingThisPost}
                                  >
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                    Change Status
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setPendingAction({
                                        type: 'delete',
                                        postId: post.id,
                                        actionName: 'delete',
                                        label: `Delete ${userLabel}'s role assignment (${roleLabel})`,
                                      })
                                      setConfirmInput('')
                                    }}
                                    disabled={isUpdatingThisPost || isCurrent}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        )
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} className="h-40">
                          <div className="flex flex-col items-center justify-center gap-3 text-center text-muted-foreground">
                            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted/60 text-foreground">
                              <ShieldCheck className="h-6 w-6" />
                            </div>
                            <div>
                              <p className="font-medium text-foreground">No assigned roles found</p>
                              <p className="mt-1 text-sm text-muted-foreground">There are no role assignments for this organization yet.</p>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {assignedRolesQuery.data ? (
                <div className="mt-3 rounded-xl border border-border/60 bg-muted/20 px-3 py-2">
                  <DataTablePagination
                    page={page}
                    pageSize={pageSize}
                    totalCount={assignedRolesQuery.data.count}
                    onPageChange={setPage}
                    onPageSizeChange={(size) => {
                      setPageSize(size)
                      setPage(1)
                    }}
                  />
                </div>
              ) : null}
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={pendingAction !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingAction(null)
            setConfirmInput('')
            setDialogNextStatus(undefined)
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {pendingAction?.type === 'delete' ? 'Delete role assignment' : 'Change assignment status'}
            </DialogTitle>
            <DialogDescription className="pt-1">
              {pendingAction?.label}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {pendingAction?.type === 'status' ? (
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">New status</p>
                <Select
                  value={dialogNextStatus !== undefined ? String(dialogNextStatus) : ''}
                  onValueChange={(v) => setDialogNextStatus(Number(v))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statusChoices.map((choice) => (
                      <SelectItem key={choice.value} value={String(choice.value)}>
                        {choice.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <p className="text-sm text-muted-foreground">
              Type{' '}
              <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs font-semibold text-foreground">
                {pendingAction?.type === 'delete' ? pendingAction.actionName : 'change'}
              </span>{' '}
              to confirm.
            </p>
            <Input
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
              placeholder={pendingAction?.type === 'delete' ? pendingAction.actionName : 'change'}
              autoFocus
              onKeyDown={(e) => {
                const keyword = pendingAction?.type === 'delete' ? pendingAction?.actionName : 'change'
                if (e.key === 'Enter' && confirmInput === keyword) {
                  if (pendingAction?.type === 'delete') {
                    deleteAssignmentMutation.mutate(pendingAction.postId)
                  } else if (pendingAction?.type === 'status' && dialogNextStatus !== undefined) {
                    updateAssignmentStatusMutation.mutate({ id: pendingAction.postId, nextStatus: dialogNextStatus })
                  }
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setPendingAction(null)
                setConfirmInput('')
              }}
            >
              Cancel
            </Button>
            <Button
              variant={pendingAction?.type === 'delete' ? 'destructive' : 'default'}
              disabled={
                confirmInput !== (pendingAction?.type === 'delete' ? pendingAction?.actionName : 'change')
                || (pendingAction?.type === 'status' && dialogNextStatus === undefined)
                || updateAssignmentStatusMutation.isPending
                || deleteAssignmentMutation.isPending
              }
              onClick={() => {
                if (!pendingAction) return
                if (pendingAction.type === 'delete') {
                  deleteAssignmentMutation.mutate(pendingAction.postId)
                } else if (pendingAction.type === 'status' && dialogNextStatus !== undefined) {
                  updateAssignmentStatusMutation.mutate({ id: pendingAction.postId, nextStatus: dialogNextStatus })
                }
              }}
            >
              {pendingAction?.type === 'delete' ? 'Delete' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}